import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, RefreshCw } from 'lucide-react';
import { listDocumentiOrdine, type DocumentoItem } from '../../api/documents';
import type { OmologaInline } from '../../types/omologa';
import apiClient from '../../api/client';

const ORIGINE_BADGE: Record<string, string> = {
  generato: 'bg-stone-mid text-ink-mid border-stone-border',
  firmato: 'bg-accent-success-bg text-accent-success border-[#A7F3D0]',
};

interface Props {
  ordineId: string;
  refreshSignal?: number;
  // R2-B: omologa dell'ordine (fonte unica omologhe.pdf_firmato_url, #105). Se firmata,
  // la card mostra una voce VIRTUALE "Omologa firmata" — NON un documents row.
  omologa?: OmologaInline | null;
}

export default function DocumentiCard({ ordineId, refreshSignal, omologa }: Props) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<DocumentoItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Voce virtuale "Omologa firmata": letta da omologhe (non da documents). Compare solo
  // se l'omologa è firmata col PDF presente.
  const omologaFirmata = omologa?.stato_firma === 'firmata' && !!omologa?.pdf_firmato_url;

  const fetchDocs = useCallback(() => {
    void (async () => {
      setLoading(true);
      try {
        setDocs(await listDocumentiOrdine(ordineId));
      } catch {
        // silently ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [ordineId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs, refreshSignal]);

  async function handleDownload(doc: DocumentoItem) {
    try {
      const resp = await apiClient.get(doc.url_download, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.tipo}-${doc.origine}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }

  // Scarico attachment della voce virtuale (fonte omologhe.pdf_firmato_url, non documents).
  async function handleDownloadOmologa() {
    if (!omologa) return;
    try {
      const resp = await apiClient.get(`/api/omologhe/${omologa.id}/firmato-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `omologa-firmata-${omologa.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-stone-mid rounded-xl p-4 animate-pulse">
        <div className="h-4 w-32 bg-stone-mid rounded mb-3" />
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 bg-stone-bg rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-dark">
          <FileText size={15} className="text-ink-soft" />
          {t('conferimenti.documenti_card.titolo')}
        </div>
        <button
          type="button"
          onClick={fetchDocs}
          title={t('conferimenti.documenti_card.aggiorna')}
          className="text-ink-soft hover:text-ink-dark transition-colors"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      <div className="p-4 space-y-2">
        {docs.length === 0 && !omologaFirmata && (
          <p className="text-xs text-ink-soft italic">{t('conferimenti.documenti_card.vuoto')}</p>
        )}

        {omologaFirmata && omologa && (
          <div
            data-testid="doc-omologa-firmata"
            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-stone-mid bg-stone-bg/50"
          >
            {/* Voce VIRTUALE: viene da `omologhe.pdf_firmato_url`, non da
                `documents`. La V68 non la deprecca — l'omologa appartiene al
                produttore ed e' condivisa fra ordini. */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-ink-dark truncate">
                  {t('conferimenti.documenti_card.omologa_firmata')}
                </p>
                <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${ORIGINE_BADGE.firmato}`}>
                  {t('conferimenti.documenti_card.origine.firmato', { defaultValue: 'firmato' })}
                </span>
              </div>
              {omologa.data_firma && (
                <p className="text-[11px] text-ink-faint">
                  {new Date(omologa.data_firma).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={handleDownloadOmologa}
                data-testid="btn-scarica-omologa-doc"
                title={t('conferimenti.documenti_card.scarica_pdf')}
                className="flex items-center gap-1 px-2 py-1 text-xs text-sage border border-sage rounded hover:bg-sage-light transition-colors"
              >
                <Download size={12} /> PDF
              </button>
            </div>
          </div>
        )}

        {docs.map((doc) => (
          <div
            key={doc.id}
            className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-stone-mid bg-stone-bg/50"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-ink-dark truncate">
                  {t(`conferimenti.documenti_card.tipo.${doc.tipo}`, { defaultValue: doc.tipo })}
                </p>
                <span
                  className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                    ORIGINE_BADGE[doc.origine] ?? 'bg-stone-mid text-ink-mid border-stone-border'
                  }`}
                >
                  {t(`conferimenti.documenti_card.origine.${doc.origine}`, { defaultValue: doc.origine })}
                </span>
                {/* V68 — un documento superato da una riapertura resta VISIBILE e
                    scaricabile: il preventivo firmato e' traccia legale (#75), la
                    firma e' avvenuta. Va pero' marcato, perche' un operatore non
                    deve poterlo scambiare per valido. */}
                {doc.deprecato_at && (
                  <span
                    data-testid="badge-documento-deprecato"
                    title={t('conferimenti.documenti_card.deprecato_nota')}
                    className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border bg-accent-warning-bg text-accent-warning border-accent-warning/40"
                  >
                    {t('conferimenti.documenti_card.deprecato')}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-ink-faint">
                {new Date(doc.generato_at).toLocaleDateString('it-IT', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
                {doc.inviato_at && (
                  <>
                    {' '}{t('conferimenti.documenti_card.inviato_il')}{' '}
                    {new Date(doc.inviato_at).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleDownload(doc)}
              title={t('conferimenti.documenti_card.scarica_pdf')}
              className="flex items-center gap-1 px-2 py-1 text-xs text-sage border border-sage rounded hover:bg-sage-light transition-colors flex-shrink-0"
            >
              <Download size={12} />
              PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
